import i18n from '@/i18n/config';
import { profileService } from './profileService';

/**
 * Language Service
 * Handles synchronization of language preferences between:
 * - i18next (UI)
 * - Supabase profiles table (database)
 * - HTML dir/lang attributes (RTL support)
 */

export const languageService = {
  /**
   * Load user's language preference from database
   * Falls back to browser language detection if no preference exists
   */
  async loadUserLanguage(userId: string): Promise<void> {
    try {
      // Fetch user profile from database
      const { data: profile, error } = await profileService.getProfile(userId);

      if (error) {
        console.error('Error loading user language:', error);
        return;
      }

      // If user has a saved language preference, apply it
      if (profile?.language) {
        await i18n.changeLanguage(profile.language);
        this.updateHTMLAttributes(profile.language);
        console.log('✅ Loaded user language from database:', profile.language);
      } else {
        // Fallback to browser language detection
        // i18next-browser-languagedetector will handle this automatically
        const detectedLang = i18n.language || 'en';
        this.updateHTMLAttributes(detectedLang);
        console.log('ℹ️ No saved language preference, using detected language:', detectedLang);
      }
    } catch (error) {
      console.error('Error in loadUserLanguage:', error);
    }
  },

  /**
   * Save user's language preference to database
   */
  async saveUserLanguage(userId: string, language: string): Promise<boolean> {
    try {
      const { error } = await profileService.updateLanguage(userId, language);

      if (error) {
        console.error('Error saving user language:', error);
        return false;
      }

      console.log('✅ Saved user language to database:', language);
      return true;
    } catch (error) {
      console.error('Error in saveUserLanguage:', error);
      return false;
    }
  },

  /**
   * Change language and sync to database
   */
  async changeLanguage(userId: string, language: string): Promise<boolean> {
    try {
      // Update i18next
      await i18n.changeLanguage(language);

      // Update HTML attributes
      this.updateHTMLAttributes(language);

      // Save to database
      await this.saveUserLanguage(userId, language);

      return true;
    } catch (error) {
      console.error('Error changing language:', error);
      return false;
    }
  },

  /**
   * Update HTML dir and lang attributes for RTL/LTR support
   */
  updateHTMLAttributes(language: string): void {
    const dir = language === 'he' ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
    document.documentElement.lang = language;
  },

  /**
   * Get current language
   */
  getCurrentLanguage(): string {
    return i18n.language || 'en';
  },

  /**
   * Check if current language is RTL
   */
  isRTL(): boolean {
    return this.getCurrentLanguage() === 'he';
  },
};
